import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Minus,
  Plus,
  Trash2,
  ShoppingCart,
  MessageCircle,
  Check,
  Copy,
  Landmark,
  ChevronRight,
  Upload,
  X,
  ImageUp,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { WA_NUMBER, bankInfo, formatRupiah } from "../mock";
import { useCart } from "../context/CartContext";
import { createOrder } from "../api";

const steps = ["Keranjang", "Data Diri", "Pembayaran"];

const Cart = () => {
  const navigate = useNavigate();
  const { items, updateQty, removeItem, total, clearCart } = useCart();
  const { toast } = useToast();
  const proofRef = useRef(null);

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ nama: "", hp: "", alamat: "", catatan: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [proof, setProof] = useState("");

  const validate = () => {
    const e = {};
    if (!form.nama.trim()) e.nama = "Nama wajib diisi";
    if (!form.hp.trim()) e.hp = "Nomor HP wajib diisi";
    else if (!/^[0-9+\-\s]{9,}$/.test(form.hp.trim())) e.hp = "Nomor HP tidak valid";
    if (!form.alamat.trim()) e.alamat = "Alamat lengkap wajib diisi";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const copyRek = () => {
    navigator.clipboard?.writeText(bankInfo.number);
    toast({ title: "Nomor rekening disalin!", description: bankInfo.number });
  };

  const handleProof = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setProof(reader.result);
    reader.readAsDataURL(file);
    if (proofRef.current) proofRef.current.value = "";
  };

  const handleConfirm = async () => {
    if (!proof) {
      toast({
        title: "Bukti pembayaran wajib diupload",
        description: "Silakan upload foto bukti transfer dulu, ya.",
      });
      return;
    }
    setSubmitting(true);
    const payload = {
      items: items.map((i) => ({
        productId: i.productId,
        name: i.name,
        variant: i.variant,
        qty: i.qty,
        price: i.price,
        image: "",
      })),
      buyer: { nama: form.nama, hp: form.hp, alamat: form.alamat },
      paymentMethod: `Transfer ${bankInfo.bank}`,
      total,
      note: form.catatan,
      paymentProof: proof,
    };

    let orderNumber = "";
    try {
      const created = await createOrder(payload);
      orderNumber = created?.orderNumber || "";
    } catch (e) {
      // still proceed to WA even if save fails
    }

    const lines = [
      "Halo ruangrasa! Saya sudah melakukan pembayaran untuk pesanan berikut:",
      "",
      orderNumber ? `No. Pesanan: ${orderNumber}` : null,
      "",
      "--- Rincian Pesanan ---",
      ...items.map(
        (i, idx) =>
          `${idx + 1}. ${i.name} (${i.variant}) x${i.qty} = ${formatRupiah(
            i.price * i.qty
          )}`
      ),
      "",
      `Total: ${formatRupiah(total)}`,
      "",
      "--- Data Pembeli ---",
      `Nama: ${form.nama}`,
      `No. HP: ${form.hp}`,
      `Alamat: ${form.alamat}`,
      form.catatan ? `Catatan: ${form.catatan}` : null,
      "",
      "--- Pembayaran ---",
      `Transfer ${bankInfo.bank} a.n. ${bankInfo.name}`,
      `No. Rek: ${bankInfo.number}`,
      "Status: Sudah transfer",
      "",
      "*Mohon saya lampirkan juga foto bukti pembayaran di chat ini ya. Terima kasih!*",
    ].filter((l) => l !== null);

    window.open(
      `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`,
      "_blank"
    );
    clearCart();
    setSubmitting(false);
    toast({
      title: "Pesanan terkirim!",
      description: orderNumber
        ? `No. Pesanan ${orderNumber}. Lanjutkan konfirmasi di WhatsApp.`
        : "Lanjutkan konfirmasi di WhatsApp.",
    });
    navigate("/");
  };

  // Empty cart
  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#E3F1EC] flex items-center justify-center px-4">
        <div className="bg-white rounded-[2.5rem] p-10 text-center shadow-xl shadow-green-900/5 max-w-sm w-full">
          <div className="w-20 h-20 rounded-3xl bg-[#E3F1ED] flex items-center justify-center mx-auto mb-5">
            <ShoppingCart className="w-9 h-9 text-[#7CC4B4]" />
          </div>
          <h2 className="text-xl font-extrabold text-[#004743]">
            Keranjang Masih Kosong
          </h2>
          <p className="text-sm text-[#6B857E] mt-2">
            Yuk, isi dindingmu dengan hiasan pilihan dari ruangrasa!
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 bg-[#004743] text-white px-6 py-3 rounded-full font-bold text-sm hover:bg-[#0A5C53] transition-colors"
          >
            Mulai Belanja
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E3F1EC] relative overflow-hidden pb-24">
      <div className="absolute top-32 -right-32 w-96 h-96 bg-[#D4EEFB] rounded-full blur-3xl opacity-60 pointer-events-none" />

      <div className="max-w-4xl mx-auto px-4 pt-28 relative">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#4A6B63] hover:text-[#004743] bg-white/70 backdrop-blur px-4 py-2 rounded-full shadow-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Lanjut Belanja
        </Link>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-1.5 md:gap-4 mt-6 mb-8">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-1.5 md:gap-2">
                <div
                  className={`w-7 h-7 md:w-8 md:h-8 shrink-0 rounded-full flex items-center justify-center text-[11px] md:text-xs font-bold transition-all duration-300 ${
                    i <= step
                      ? "bg-[#FD7A18] text-white"
                      : "bg-white text-[#AEC6BE]"
                  }`}
                >
                  {i < step ? <Check className="w-3.5 h-3.5 md:w-4 md:h-4" /> : i + 1}
                </div>
                <span
                  className={`text-[11px] md:text-sm font-bold whitespace-nowrap ${
                    i <= step ? "text-[#004743]" : "text-[#AEC6BE]"
                  }`}
                >
                  {s}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`h-0.5 w-4 md:w-12 shrink-0 rounded-full ${
                    i < step ? "bg-[#FD7A18]" : "bg-[#CFE7E0]"
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 0 - cart items */}
        {step === 0 && (
          <div className="space-y-4">
            {items.map((i) => (
              <div
                key={i.key}
                className="bg-white rounded-[1.75rem] p-4 flex items-center gap-4 shadow-lg shadow-green-900/5"
              >
                <div className="w-20 h-20 rounded-2xl overflow-hidden bg-[#E3F1ED] shrink-0">
                  {i.image ? (
                    <img
                      src={i.image}
                      alt={i.name}
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-[#004743] text-sm line-clamp-1">
                    {i.name}
                  </h3>
                  <p className="text-[11px] text-[#8AA59D] mt-0.5">
                    Varian: {i.variant}
                  </p>
                  <p className="text-base font-extrabold text-[#0B7C6E] mt-1">
                    {formatRupiah(i.price * i.qty)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <button
                    onClick={() => removeItem(i.key)}
                    className="text-[#C98A8A] hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-2 bg-[#F0F7F5] border border-[#CFE7E0] rounded-full px-1.5 py-1">
                    <button
                      onClick={() => updateQty(i.key, i.qty - 1)}
                      className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center text-[#004743]"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="font-bold text-[#004743] w-5 text-center text-sm">
                      {i.qty}
                    </span>
                    <button
                      onClick={() => updateQty(i.key, i.qty + 1)}
                      className="w-7 h-7 rounded-full bg-[#0B7C6E] shadow-sm flex items-center justify-center text-white"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            <div className="bg-white rounded-[1.75rem] p-5 flex items-center justify-between shadow-lg shadow-green-900/5">
              <span className="font-semibold text-[#4A6B63]">Total</span>
              <span className="text-2xl font-extrabold text-[#0B7C6E]">
                {formatRupiah(total)}
              </span>
            </div>

            <button
              onClick={() => setStep(1)}
              className="w-full bg-[#FD7A18] hover:bg-[#E56A0E] text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30 hover:-translate-y-0.5 transition-all duration-300"
            >
              Lanjut Isi Data
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* STEP 1 - form */}
        {step === 1 && (
          <div className="bg-white rounded-[2rem] p-6 md:p-8 shadow-lg shadow-green-900/5">
            <h2 className="text-lg font-extrabold text-[#004743] mb-4">
              Isi Data Lengkap
            </h2>
            <div className="space-y-3">
              {[
                { k: "nama", ph: "Nama Lengkap", type: "input" },
                { k: "hp", ph: "Nomor HP / WhatsApp", type: "input" },
                { k: "alamat", ph: "Alamat Lengkap (jalan, kecamatan, kota, kode pos)", type: "textarea" },
                { k: "catatan", ph: "Catatan (opsional)", type: "input" },
              ].map((f) => (
                <div key={f.k}>
                  {f.type === "textarea" ? (
                    <textarea
                      rows={3}
                      className={`w-full bg-[#F0F7F5] border ${
                        errors[f.k] ? "border-red-300" : "border-[#CFE7E0]"
                      } rounded-2xl px-4 py-3 text-sm text-[#004743] placeholder-[#A3C1B8] focus:outline-none focus:ring-2 focus:ring-[#7CC4B4]/50 focus:border-[#7CC4B4] transition-all resize-none`}
                      placeholder={f.ph}
                      value={form[f.k]}
                      onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    />
                  ) : (
                    <input
                      className={`w-full bg-[#F0F7F5] border ${
                        errors[f.k] ? "border-red-300" : "border-[#CFE7E0]"
                      } rounded-2xl px-4 py-3 text-sm text-[#004743] placeholder-[#A3C1B8] focus:outline-none focus:ring-2 focus:ring-[#7CC4B4]/50 focus:border-[#7CC4B4] transition-all`}
                      placeholder={f.ph}
                      value={form[f.k]}
                      onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    />
                  )}
                  {errors[f.k] && (
                    <p className="text-[11px] text-red-500 mt-1 ml-1">
                      {errors[f.k]}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep(0)}
                className="px-6 py-3.5 rounded-full font-bold text-sm text-[#4A6B63] bg-[#F0F7F5] hover:bg-[#E3F1ED] transition-colors"
              >
                Kembali
              </button>
              <button
                onClick={() => validate() && setStep(2)}
                className="flex-1 bg-[#FD7A18] hover:bg-[#E56A0E] text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-orange-500/30 hover:-translate-y-0.5 transition-all duration-300"
              >
                Lanjut ke Pembayaran
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 - payment */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-white rounded-[2rem] p-6 md:p-8 shadow-lg shadow-green-900/5">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl bg-[#004743] flex items-center justify-center">
                  <Landmark className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-extrabold text-[#004743]">
                    Pembayaran Transfer Bank
                  </h2>
                  <p className="text-xs text-[#8AA59D]">
                    Silakan transfer sesuai total di bawah
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#004743] to-[#0A5C53] rounded-3xl p-6 text-white relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/5 rounded-full" />
                <p className="text-xs text-white/70 font-semibold">
                  Bank {bankInfo.bank}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-2xl font-extrabold tracking-wider">
                    {bankInfo.number}
                  </p>
                  <button
                    onClick={copyRek}
                    className="bg-[#FD7A18] hover:bg-[#E56A0E] text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1.5 transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Salin
                  </button>
                </div>
                <p className="text-sm text-white/90 mt-2 font-semibold">
                  a.n. {bankInfo.name}
                </p>
                <div className="border-t border-white/15 mt-4 pt-4 flex items-center justify-between">
                  <span className="text-sm text-white/70">Total Transfer</span>
                  <span className="text-xl font-extrabold text-[#FFD66B]">
                    {formatRupiah(total)}
                  </span>
                </div>
              </div>

              <div className="bg-[#FFF1E6] rounded-2xl p-4 mt-4">
                <p className="text-xs text-[#8a5220] leading-relaxed">
                  <span className="font-bold text-[#E8630C]">Penting:</span>{" "}
                  Setelah transfer, klik tombol di bawah untuk mengirim rincian
                  pesanan & konfirmasi pembayaran ke admin via WhatsApp. Kirim
                  juga bukti transfermu di chat, ya!
                </p>
              </div>
            </div>

            {/* Upload bukti pembayaran */}
            <div className="bg-white rounded-[2rem] p-6 shadow-lg shadow-green-900/5">
              <div className="flex items-center gap-2 mb-1">
                <ImageUp className="w-5 h-5 text-[#0B7C6E]" />
                <h3 className="font-bold text-[#004743] text-sm">
                  Upload Bukti Pembayaran
                </h3>
                <span className="text-[#FD7A18] text-sm font-bold">*</span>
              </div>
              <p className="text-xs text-[#8AA59D] mb-3">
                Wajib diupload dulu sebelum konfirmasi ke WhatsApp.
              </p>

              <input
                ref={proofRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleProof}
              />

              {proof ? (
                <div className="relative rounded-2xl overflow-hidden border border-[#CFE7E0] bg-[#F0F7F5]">
                  <img
                    src={proof}
                    alt="Bukti pembayaran"
                    className="w-full max-h-64 object-contain"
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={() => proofRef.current?.click()}
                      className="bg-white/90 text-[#004743] text-xs font-bold px-3 py-1.5 rounded-full shadow hover:bg-white transition-colors"
                    >
                      Ganti
                    </button>
                    <button
                      onClick={() => setProof("")}
                      className="w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <span className="absolute bottom-2 left-2 bg-[#0B7C6E] text-white text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Bukti terupload
                  </span>
                </div>
              ) : (
                <button
                  onClick={() => proofRef.current?.click()}
                  className="w-full border-2 border-dashed border-[#CFE7E0] rounded-2xl py-8 flex flex-col items-center justify-center gap-2 text-[#7CC4B4] hover:border-[#FD7A18] hover:text-[#FD7A18] transition-colors"
                >
                  <Upload className="w-7 h-7" />
                  <span className="text-sm font-bold">Pilih Foto Bukti Transfer</span>
                  <span className="text-[11px] text-[#8AA59D]">
                    Format JPG / PNG
                  </span>
                </button>
              )}
            </div>

            {/* summary */}
            <div className="bg-white rounded-[2rem] p-6 shadow-lg shadow-green-900/5">
              <h3 className="font-bold text-[#004743] text-sm mb-3">
                Ringkasan Pesanan
              </h3>
              <div className="space-y-2">
                {items.map((i) => (
                  <div
                    key={i.key}
                    className="flex justify-between text-sm text-[#6B857E]"
                  >
                    <span className="truncate mr-2">
                      {i.name} ({i.variant}) x{i.qty}
                    </span>
                    <span className="font-semibold text-[#004743] shrink-0">
                      {formatRupiah(i.price * i.qty)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t border-dashed border-[#CFE7E0] mt-3 pt-3 flex justify-between">
                <span className="font-bold text-[#004743]">Total</span>
                <span className="font-extrabold text-[#0B7C6E]">
                  {formatRupiah(total)}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-4 rounded-full font-bold text-sm text-[#4A6B63] bg-white hover:bg-[#E3F1ED] transition-colors shadow-lg shadow-green-900/5"
              >
                Kembali
              </button>
              <button
                onClick={handleConfirm}
                disabled={submitting || !proof}
                className="flex-1 bg-[#0B7C6E] hover:bg-[#096A5E] text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-lg shadow-green-600/30 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
              >
                <MessageCircle className="w-5 h-5" />
                {submitting
                  ? "Memproses..."
                  : !proof
                  ? "Upload Bukti Dulu"
                  : "Konfirmasi & Kirim ke WhatsApp"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
